import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, Tag } from "lucide-react";
import { getActiveProjectGraph } from "../../data/sampleGraph";
import { NODE_PALETTE } from "../../types/ontology";

interface NodeDetailProps {
  nodeId: string | null;
  onClose: () => void;
  onNavigate?: (nodeId: string) => void;
}

export function NodeDetail({ nodeId, onClose, onNavigate }: NodeDetailProps): JSX.Element | null {
  if (!nodeId) return null;
  const node = getActiveProjectGraph().nodes.find((n) => n.id === nodeId);
  if (!node) return null;
  const palette = NODE_PALETTE[node.kind];

  const outgoing = getActiveProjectGraph().edges.filter((e) => e.source === nodeId);
  const incoming = getActiveProjectGraph().edges.filter((e) => e.target === nodeId);

  const attrs = Object.entries(node.attrs).filter(([, v]) => v !== null && v !== "");

  return (
    <AnimatePresence>
      <motion.aside
        key={nodeId}
        initial={{ opacity: 0, y: 12, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 8 }}
        transition={{ duration: 0.18 }}
        className="panel p-4 space-y-3"
      >
        <header className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <span
              className="w-4 h-4 rounded-full border-2 shrink-0"
              style={{ background: palette.hex, borderColor: palette.hex }}
            />
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-ink-500">{node.kind}</div>
              <div className="text-sm font-semibold text-ink-950 truncate">{node.label}</div>
            </div>
          </div>
          <button onClick={onClose} className="text-ink-500 hover:text-ink-950 p-1 -m-1">
            <X className="w-3.5 h-3.5" />
          </button>
        </header>

        {attrs.length > 0 ? (
          <section>
            <div className="eyebrow mb-1.5">Attributes</div>
            <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-[11px]">
              {attrs.slice(0, 12).map(([k, v]) => (
                <div key={k} className="min-w-0">
                  <dt className="text-ink-500 font-mono text-[10px] truncate">{k}</dt>
                  <dd className="text-ink-950 font-medium tabular truncate">{formatValue(v)}</dd>
                </div>
              ))}
            </dl>
          </section>
        ) : null}

        {outgoing.length > 0 ? (
          <section>
            <div className="eyebrow mb-1.5">→ Outgoing ({outgoing.length})</div>
            <ul className="space-y-1">
              {outgoing.slice(0, 6).map((e) => {
                const target = getActiveProjectGraph().nodes.find((n) => n.id === e.target);
                if (!target) return null;
                return (
                  <li key={e.id}>
                    <button
                      onClick={() => onNavigate?.(target.id)}
                      className="w-full flex items-center gap-1.5 text-[11px] text-left hover:bg-cream-dark rounded-md px-1.5 py-1 transition"
                    >
                      <span className="font-mono text-[9px] text-ink-500 shrink-0">{e.kind.toLowerCase()}</span>
                      <ArrowRight className="w-3 h-3 text-ink-300 shrink-0" />
                      <span className="text-ink-950 truncate">{target.label}</span>
                      <Tag className="w-2.5 h-2.5 text-ink-300 ml-auto shrink-0" />
                    </button>
                  </li>
                );
              })}
              {outgoing.length > 6 ? <li className="text-[10px] text-ink-500 px-1.5">+ {outgoing.length - 6} more</li> : null}
            </ul>
          </section>
        ) : null}

        {incoming.length > 0 ? (
          <section>
            <div className="eyebrow mb-1.5">← Incoming ({incoming.length})</div>
            <ul className="space-y-1">
              {incoming.slice(0, 6).map((e) => {
                const source = getActiveProjectGraph().nodes.find((n) => n.id === e.source);
                if (!source) return null;
                return (
                  <li key={e.id}>
                    <button
                      onClick={() => onNavigate?.(source.id)}
                      className="w-full flex items-center gap-1.5 text-[11px] text-left hover:bg-cream-dark rounded-md px-1.5 py-1 transition"
                    >
                      <span className="text-ink-950 truncate">{source.label}</span>
                      <ArrowRight className="w-3 h-3 text-ink-300 shrink-0 rotate-180" />
                      <span className="font-mono text-[9px] text-ink-500 shrink-0">{e.kind.toLowerCase()}</span>
                    </button>
                  </li>
                );
              })}
              {incoming.length > 6 ? <li className="text-[10px] text-ink-500 px-1.5">+ {incoming.length - 6} more</li> : null}
            </ul>
          </section>
        ) : null}

        <div className="text-[10px] text-ink-500 font-mono pt-2 border-t border-border">
          {node.id}
        </div>
      </motion.aside>
    </AnimatePresence>
  );
}

function formatValue(v: unknown): string {
  if (typeof v === "number") {
    if (v >= 1000) return v.toLocaleString("en-IN");
    if (Math.abs(v) < 1 && v !== 0) return v.toFixed(2);
    return String(v);
  }
  if (typeof v === "boolean") return v ? "true" : "false";
  return String(v);
}
